import React from 'react';
import { useTranslation } from 'react-i18next';
import CreateApplication from 'Modules/patientfi/routes/CreateApplication';
import FinancingPreparation from 'Modules/patientfi/components/FinancingPreparation';
import { useGetNextStep } from 'Modules/patientfi/hooks/useGetNextStep';
import { useGetPaymentInfo } from 'Core/hooks/platform/useGetPaymentInfo';
import Loader from 'Core/components/Loader';
import FinancingSuccess from 'Modules/patientfi/routes/FinancingSuccess';
import FinancingApproved from 'Modules/patientfi/routes/FinancingApproved';
import ReturnTracking from 'Modules/patientfi/components/ReturnTracking';
import FinancingStatusMessage from 'Modules/patientfi/components/FinancingStatusMessage';
import FinancingDeclined from 'Modules/patientfi/routes/FinancingDeclined';
import FinancingExceptions from 'Modules/patientfi/routes/FinancingExceptions';
import FinancingExistingAccount from 'Modules/patientfi/routes/FinancingExistingAccount';
import FinancingDenied from 'Modules/patientfi/routes/ExistingAccount/FinancingDenied';
// import FinancingDecisionPending from 'Modules/patientfi/routes/FinancingDecisionPending';

import { cardPreferenceCenterPath, paymentPath } from 'Modules/cardPayment/routes';
import constants from 'Modules/patientfi/config/constants';
import {
  financingPath,
  financingSuccessPath,
  financingErrorPath,
  mixedDownPaymentCheckoutPath,
} from 'Modules/patientfi/routes';
import { errorPath } from 'Core/routes';
import useErrorStore, { setErrorActionSelector } from 'Core/stores/error';
import { useNavigate } from 'react-location';
import { createFinancingPath as allegroFinancingPath } from 'Modules/financing/routes';

export default function FinancingRedirect() {
  const routes = {
    paymentOptions: cardPreferenceCenterPath,
    financing: financingPath,
    mixed: mixedDownPaymentCheckoutPath,
    financingSuccessPath,
    financingErrorPath,
    payment: paymentPath,
    allegroFinancing: allegroFinancingPath,
  };
  const navigate = useNavigate();
  const setError = useErrorStore(setErrorActionSelector);
  const { t } = useTranslation();
  const rawSessionStorageItem = sessionStorage.getItem(constants.SESS_STORAGE_APPLICATION_KEY);
  const cachedApp =
    rawSessionStorageItem && rawSessionStorageItem !== 'undefined'
      ? JSON.parse(rawSessionStorageItem)
      : null;
  const { data: newApp, isLoading, isError, error } = useGetNextStep(false, !cachedApp);
  const application = cachedApp || newApp;
  sessionStorage.removeItem(constants.SESS_STORAGE_APPLICATION_KEY);
  const { next_action = 'CREATE_APPLICATION' } = application || {};
  const {
    data: { amount_to_pay, customer_id = '', provider = '' } = {},
    isLoading: isPaymentInfoLoading,
    isError: isPaymentInfoError,
  } = useGetPaymentInfo();

  if (isLoading) {
    return <Loader message={t('status.loading.financingApplication')} />;
  } else if (provider && provider !== 'PATIENTFI') {
    navigate({ to: routes.allegroFinancing, search: true });
  }

  // No application found? -> Show form to create one
  else if (isError || error?.response?.status === 404 || !application?.next_action) {
    return <CreateApplication customer_id={customer_id} />;
  } else if (application?.next_action) {
    switch (next_action) {
      case 'CREATE_APPLICATION':
        return <CreateApplication type="primary" customer_id={customer_id} />;
      case 'SELECT_OFFER':
      case 'UPDATE_OFFER':
      case 'SIGN_CONTRACT':
        return <FinancingPreparation application={cachedApp} routes={routes} />;
      case 'CORRECTION_REQUIRED':
      case 'DECLINED':
      case 'DECISION_PENDING':
      case 'INCOMPLETE':
        return (
          <FinancingExceptions
            customer_id={customer_id}
            application={application}
            nextAction={next_action}
          />
        );
      case 'UPDATE_EXISTING_ACCOUNT':
        if (application?.payload?.existing_account?.account_status === 'DENIED') {
          return <FinancingDenied application={application} />;
        }
        return <FinancingExistingAccount application={application} />;
      case 'UPDATE_AMOUNT':
        return <FinancingApproved />;
      case 'CANCELLED':
        return <FinancingDeclined application={application} />;
      case 'DONE':
      case 'FUND_APPLICATION':
      case 'PENDING_DOWN_PAYMENT':
        return <FinancingSuccess />;
      case 'RETURN_APPROVED':
      case 'RETURN_REQUESTED':
      case 'RETURN_PROCESSING':
      case 'RETURN_COMPLETE':
      case 'RETURN_CANCELED':
        return <ReturnTracking nextAction={next_action} />;
      case 'NO_DECISION':
      case 'APPLICATION_CLOSED':
        return <FinancingStatusMessage nextAction={next_action} />;
      default:
        setError(t('status.error.getFinancingApplicationFailed'));
        navigate({ to: errorPath, search: true });
    }
  }

  if (!isPaymentInfoError && !isPaymentInfoLoading && !amount_to_pay) {
    return <FinancingApproved />;
  }

  return <Loader message={t('status.loading.financingApplication')} />;
}
